import { useState } from "react";
import { useSettings } from "@/hooks/useDomains";
import { changePassword, saveSettings } from "@/lib/store";
import type { RedirectType } from "@/types";

export default function Settings() {
  const { settings } = useSettings();
  const [defaultType, setDefaultType] = useState<RedirectType>(settings.defaultRedirectType);
  const [fallbackUrl, setFallbackUrl] = useState(settings.fallbackUrl);
  const [autoRedirect, setAutoRedirect] = useState(settings.autoRedirect);
  const [trackClicks, setTrackClicks] = useState(settings.trackClicks);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [nextPassword, setNextPassword] = useState("");
  const [passwordMessage, setPasswordMessage] = useState("");
  const [passwordError, setPasswordError] = useState("");

  const handleSave = () => {
    void saveSettings({
      defaultRedirectType: defaultType,
      fallbackUrl,
      autoRedirect,
      trackClicks,
    }).then((result) => {
      if (!result.ok) {
        setSaved(false);
        setError(result.message);
        return;
      }
      setError("");
      setSaved(true);
      window.setTimeout(() => setSaved(false), 2200);
    });
  };

  const handlePassword = () => {
    setPasswordError("");
    setPasswordMessage("");
    void changePassword(currentPassword, nextPassword).then((result) => {
      if (!result.ok) {
        setPasswordError(result.message);
        return;
      }
      setCurrentPassword("");
      setNextPassword("");
      setPasswordMessage(result.message || "密码已更新");
    });
  };

  const TYPE_OPTIONS: { value: RedirectType; label: string; desc: string }[] = [
    { value: "301", label: "301 永久跳转", desc: "搜索引擎更新索引，长期方案" },
    { value: "302", label: "302 临时跳转", desc: "不改变索引，活动引流推荐" },
    { value: "mask", label: "隐藏跳转", desc: "地址栏保持中转域名不变" },
  ];

  return (
    <div className="flex max-w-4xl flex-col gap-6">
      <div className="rounded-lg border border-background-200 bg-background-50 p-6">
        <h3 className="font-heading text-base font-semibold text-foreground-950">默认跳转设置</h3>
        <p className="mt-1 text-sm text-foreground-500">新增域名时默认使用的跳转方式</p>
        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
          {TYPE_OPTIONS.map((opt) => {
            const selected = defaultType === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => setDefaultType(opt.value)}
                className={`rounded-md border px-4 py-3 text-left transition-colors cursor-pointer ${
                  selected ? "border-primary-500 bg-primary-100" : "border-background-300 bg-background-50 hover:border-primary-300"
                }`}
              >
                <span className="flex items-center justify-between">
                  <span className="text-sm font-medium text-foreground-900">{opt.label}</span>
                  {selected ? <i className="ri-check-line text-primary-700" /> : null}
                </span>
                <span className="mt-1 block text-xs text-foreground-500">{opt.desc}</span>
              </button>
            );
          })}
        </div>

        <label className="mt-5 flex flex-col gap-2">
          <span className="font-label text-sm font-medium text-foreground-800">失效跳转兜底地址</span>
          <input
            type="text"
            value={fallbackUrl}
            onChange={(e) => setFallbackUrl(e.target.value)}
            className="rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-950 outline-none focus:border-primary-500"
          />
          <span className="text-xs text-foreground-500">域名暂停、被删除，或测试链接无效时，访客会去到这个地址</span>
        </label>
      </div>

      <div className="rounded-lg border border-background-200 bg-background-50 p-6">
        <h3 className="font-heading text-base font-semibold text-foreground-950">运行选项</h3>
        <div className="mt-4 flex flex-col gap-4">
          {[
            { label: "自动启用新域名", desc: "新增域名后自动开始跳转，无需手动开启", value: autoRedirect, set: setAutoRedirect },
            { label: "统计点击数据", desc: "记录每个域名的访问量用于数据分析", value: trackClicks, set: setTrackClicks },
          ].map((row) => (
            <div key={row.label} className="flex items-center justify-between gap-4 rounded-md bg-background-100 px-4 py-3.5">
              <div>
                <p className="text-sm font-medium text-foreground-900">{row.label}</p>
                <p className="mt-0.5 text-xs text-foreground-500">{row.desc}</p>
              </div>
              <button
                type="button"
                onClick={() => row.set(!row.value)}
                aria-pressed={row.value}
                className={`relative h-6 w-11 shrink-0 rounded-full transition-colors cursor-pointer ${
                  row.value ? "bg-primary-500" : "bg-background-400"
                }`}
              >
                <span
                  className={`absolute top-0.5 h-5 w-5 rounded-full bg-background-50 transition-all ${
                    row.value ? "left-[22px]" : "left-0.5"
                  }`}
                />
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-lg border border-background-200 bg-background-50 p-6">
        <h3 className="font-heading text-base font-semibold text-foreground-950">管理员密码</h3>
        <p className="mt-1 text-sm text-foreground-500">修改后立即生效，当前登录会继续保持</p>
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <label className="flex flex-col gap-2">
            <span className="font-label text-sm font-medium text-foreground-800">当前密码</span>
            <input
              type="password"
              value={currentPassword}
              onChange={(event) => setCurrentPassword(event.target.value)}
              className="rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-950 outline-none focus:border-primary-500"
            />
          </label>
          <label className="flex flex-col gap-2">
            <span className="font-label text-sm font-medium text-foreground-800">新密码</span>
            <input
              type="password"
              value={nextPassword}
              onChange={(event) => setNextPassword(event.target.value)}
              className="rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-950 outline-none focus:border-primary-500"
            />
          </label>
        </div>
        <div className="mt-4 flex items-center gap-3">
          <button
            type="button"
            onClick={handlePassword}
            className="rounded-md border border-background-300 px-4 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-300 cursor-pointer whitespace-nowrap"
          >
            更新密码
          </button>
          {passwordError ? <span className="text-sm text-secondary-800">{passwordError}</span> : null}
          {passwordMessage ? <span className="text-sm text-accent-700">{passwordMessage}</span> : null}
        </div>
      </div>

      <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2 text-xs text-foreground-500">
          <i className="ri-information-line" />
          设置保存在运行服务的电脑上。新增域名会使用这里的默认跳转方式和启用状态。
        </div>
        <div className="flex items-center gap-3">
          {error ? <span className="text-sm text-secondary-800">{error}</span> : null}
          {saved ? (
            <span className="flex items-center gap-1.5 text-sm text-accent-700">
              <i className="ri-checkbox-circle-line" />
              已保存
            </span>
          ) : null}
          <button
            type="button"
            onClick={handleSave}
            className="rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer whitespace-nowrap"
          >
            保存设置
          </button>
        </div>
      </div>
    </div>
  );
}